import { Link } from 'react-router';
import { deriveItemType, type PublicItem } from '@im-marina/shared';
import { fallbackSrc, toSrcSet } from '../../lib/media';
import styles from './ItemCard.module.css';

/** Height reserved under each card when captions are always shown (touch devices). */
export const CAPTION_HEIGHT = 44;

type Props = { item: PublicItem; columns: number; showCaption: boolean };

export function ItemCard({ item, columns, showCaption }: Props) {
  const type = deriveItemType(item.media);
  const { cover } = item;
  const sizes = columns === 1 ? '100vw' : `${Math.ceil(100 / columns)}vw`;
  const meta = [item.year, type !== 'image' ? describe(type, item.media.length) : null]
    .filter(Boolean)
    .join(' · ');

  return (
    <Link
      to={{ search: `?item=${item.slug}` }}
      state={{ fromGrid: true }}
      preventScrollReset
      className={styles.card}
      data-show-caption={showCaption || undefined}
    >
      <div className={styles.frame}>
        <img
          className={styles.image}
          src={fallbackSrc(cover)}
          srcSet={toSrcSet(cover.srcset)}
          sizes={sizes}
          width={cover.width}
          height={cover.height}
          alt={cover.alt || item.title}
          loading="lazy"
          decoding="async"
        />
        {type !== 'image' && <TypeBadge type={type} count={item.media.length} />}
        <div className={styles.overlay} aria-hidden>
          <span className={styles.title}>{item.title}</span>
          {meta && <span className={styles.meta}>{meta}</span>}
        </div>
      </div>
      {showCaption && (
        <div className={styles.caption}>
          <span className={styles.title}>{item.title}</span>
          {item.year && <span className={styles.meta}>{item.year}</span>}
        </div>
      )}
    </Link>
  );
}

function describe(type: ReturnType<typeof deriveItemType>, count: number) {
  if (type === 'video') return 'Video';
  if (type === 'gallery') return `${count} images`;
  return `Video + ${count - 1}`;
}

function TypeBadge({ type, count }: { type: ReturnType<typeof deriveItemType>; count: number }) {
  const hasVideo = type === 'video' || type === 'video-gallery';
  const hasGallery = type === 'gallery' || type === 'video-gallery';
  return (
    <span className={styles.badge}>
      {hasVideo && (
        <svg viewBox="0 0 12 12" width="10" height="10" aria-hidden>
          <path d="M3 1.5v9l7.5-4.5z" fill="currentColor" />
        </svg>
      )}
      {hasGallery && (
        <>
          <svg viewBox="0 0 12 12" width="11" height="11" aria-hidden>
            <rect x="3.5" y="0.5" width="8" height="8" fill="none" stroke="currentColor" />
            <path d="M0.5 3.5v8h8" fill="none" stroke="currentColor" />
          </svg>
          <span>{count}</span>
        </>
      )}
      <span className="visually-hidden">{describe(type, count)}</span>
    </span>
  );
}
